import { inject, Injectable, NgZone } from '@angular/core';
import { CryptoApi, GeneratedSecretStorageKey } from 'matrix-js-sdk/lib/crypto-api';
import { deriveRecoveryKeyFromPassphrase } from 'matrix-js-sdk/lib/crypto-api/key-passphrase';
import { decodeRecoveryKey } from 'matrix-js-sdk/lib/crypto-api/recovery-key';
import { MatrixClient } from 'matrix-js-sdk';
import { SnackbarService } from '../snackbar/snackbar.service';
import { KeyBackupCheck, KeyBackupInfo } from 'matrix-js-sdk/lib/crypto-api/keybackup';
import { MatrixService } from '../matrix/matrix.service';

@Injectable({
  providedIn: 'root'
})
/**
 * Service-Klasse für Verifikationsmethoden ohne direkten Bezug zur GUI.
 * Größtenteils handelt es sich um Methoden zur Initialisierung von
 * *Secure Storage* und *Cross Signing*.
 *
 * Hierdurch erfüllte AFO's:
 *
 *  A_25616 - Parameter für die passwort-basierte Schlüsselableitung
 *  A_26575 - Ablage von Schlüsseln zum Entschlüsseln von Nachrichten nach Wiederanmeldung
 */
export class VerificationService {
  // Backup-Operationen können direkt nach einer Schlüsseländerung/einem Reset transient scheitern,
  // weil die neue Backup-Version serverseitig noch nicht propagiert ist (eventually consistent).
  // Solche Fehler werden begrenzt mit Backoff wiederholt; die Gesamtzeit bleibt klein genug, um den
  // synchron aufrufenden Login-Pfad nicht über Gebühr zu blockieren.
  private static readonly KEY_BACKUP_RETRY_ATTEMPTS = 4;
  private static readonly KEY_BACKUP_RETRY_DELAYS_MS = [1_000, 2_000, 4_000];

  private readonly clientService = inject(MatrixService);
  private readonly snackbarService = inject(SnackbarService);
  private readonly zone = inject(NgZone);

  /**
   * Prüft deterministisch (MAC-Vergleich gegen den Secret-Storage-Schlüssel), ob die eingegebene
   * Passphrase bzw. der Recovery-Key zum hinterlegten Secret Storage passt — OHNE Server-/Backup-Zugriff.
   * Dient dazu, ein echtes Fehlpasswort sicher von transienten Backup-Konsistenzfehlern zu trennen
   * (Letztere erzeugen denselben "does not match"-Fehlertext und dürfen nicht als Fehlpasswort gelten).
   * Liefert true, wenn (noch) kein Secret Storage existiert (Erst-Einrichtung — nichts zu prüfen).
   */
  public async verifyRecoveryKey(passphrase: string): Promise<boolean> {
    const client = this.clientService.client;
    if (!client) {
      throw new Error('Es ist kein Client aktiv.');
    }

    const keyTuple = await client.secretStorage.getKey();
    if (!keyTuple) {
      return true;
    }
    const [, info] = keyTuple;

    let key: Uint8Array;
    try {
      key = info.passphrase
        ? await deriveRecoveryKeyFromPassphrase(passphrase, info.passphrase.salt, info.passphrase.iterations)
        : decodeRecoveryKey(passphrase);
    } catch {
      // Nicht ableitbar bzw. ungültig kodiert → Schlüssel ist nicht korrekt.
      return false;
    }
    return await client.secretStorage.checkKey(key, info);
  }

  /**
   * Initialisiert den serverseitigen Secret-Storage, indem bspw. die Schlüsselsicherung abgelegt wird
   * @param reset Gibt an, ob der Secret-Storage mit neuen Schlüsseln reinitialisiert werden soll
   */
  public async initSecretStorage(reset: boolean): Promise<void> {
    const client = this.clientService.client;
    if (!client) {
      throw new Error('Es ist kein Client aktiv.');
    }
    const crypto = client.getCrypto();
    if (!crypto) {
      throw new Error('Es ist keine Krypto-Api vorhanden.');
    }

    const isReady = await crypto.isSecretStorageReady();
    if (isReady && !reset) {
      return;
    }
    if (reset) {
      await this.bootstrapSecretStorageWithReset(crypto);
    } else {
      await this.bootstrapSecretStorageWithoutReset(crypto);
    }
  }

  /**
   * Initialisiert das Cross-Signing, das für die eigene Verifikaiton als auch die Verifikation anderer Devices desselben
   * Users erforderlich ist. Konkret werden dabei mehrere Schlüsselpaare erzeugt:
   * <ul>
   *     <li>m.cross_signing.master</li>
   *     <li>m.cross_signing.self_signing</li>
   *     <li>m.cross_signing.user_signing</li>
   *     <li>m.megolm_backup.v1</li>
   * </ul>
   * @param reset gibt an, ob neue Cross-Signing-Schlüssel generiert werden sollen (also ein Reset ausgeführt werden soll)
   */
  public async initCrossSigning(reset: boolean): Promise<boolean | void> {
    const client = this.clientService.client;
    if (!client) {
      throw new Error('Kein Client aktiv');
    }
    const crypto = client.getCrypto();
    if (!crypto) {
      throw new Error('Keine Krypto-api');
    }
    const isReady = await crypto.isCrossSigningReady();
    try {
      return await this.processCrossSigning(isReady, reset, crypto, client);
    } catch (e: any) {
      this.snackbarService.error('Es gab einen Fehler bei der Initialisierung des Cross-Signings');
      throw new Error(`Fehler bei der Initialisierung des Cross-Signing. ${e}`);
    }
  }

  /**
   * Aktiviert die serverseitige Schlüsselsicherung und stellt eine vorhandene Schlüsselsicherung per Aufruf von
   * restoreKeyBackupWithSecretStorage wieder her. Da Schlüssel nur in ein Backup gesichert werden dem vertraut wird,
   * aber dies nach einem Restore nicht der Fall ist, wird hier zunächst immer eine neue Schlüsselsicherung angelegt.
   * Ein ähnliches Thema wurde auch bspw. hier diskutiert: https://github.com/element-hq/element-web/issues/11404
   */

  public async initKeyBackup(reset: boolean): Promise<KeyBackupCheck | null> {
    const client = this.clientService.client;
    if (!client) {
      throw new Error('Kein Client');
    }
    const crypto = client.getCrypto();
    if (!crypto) {
      throw new Error('Keine Krypto');
    }
    try {
      const keybackupInfo = await crypto.getKeyBackupInfo();
      if (keybackupInfo) {
        // processKeyBackup greift auf die serverseitige Backup-Version zu und kann unmittelbar nach
        // einer Schlüsseländerung transient scheitern → begrenzt wiederholen, bis propagiert.
        const keybackupCheck = await this.retryOnTransientBackupError(() =>
          this.processKeyBackup(crypto, keybackupInfo)
        );
        if (!keybackupCheck?.trustInfo.trusted || reset) {
          return await this.resetAndEnableKeyBackup(crypto);
        } else {
          return null;
        }
      } else {
        return null;
      }
    } catch (e: any) {
      this.snackbarService.error('Konnte Schlüsselsicherung nicht initialisieren.');
      throw new Error(`Fehler bei dem Erstellen der Schlüsselsicherung. ${e}`);
    }
  }

  public async hasKeyBackup(): Promise<KeyBackupInfo | null> {
    const client = this.clientService.getClientSync();
    if (!client) {
      return null;
    }
    const crypto = client.getCrypto();
    if (!crypto) {
      return null;
    }
    return await crypto.getKeyBackupInfo();
  }

  async openReAuth(sessionId: string): Promise<void> {
    return new Promise((resolve, reject) => {
      const client = this.clientService.getClientSync();
      const fallbackAuthUrl = client?.getFallbackAuthUrl('m.login.sso', sessionId);
      const popup = globalThis.open(fallbackAuthUrl);
      if (!popup) {
        reject(new Error('Popup konnte nicht geöffnet werden. Bitte Popup-Blocker deaktivieren.'));
        return;
      }
      const cleanup = (): void => {
        clearInterval(interval);
        window.removeEventListener('message', messageListener);
      };
      const interval = setInterval(() => {
        if (popup.closed) {
          cleanup();
          reject(new Error('Authentifizierungsfenster wurde geschlossen.'));
        }
      }, 500);
      const expectedOrigin = fallbackAuthUrl ? new URL(fallbackAuthUrl).origin : undefined;
      const messageListener = (event: MessageEvent): void => {
        if (expectedOrigin && event.origin !== expectedOrigin) {
          return;
        }
        this.zone.run(() => {
          if (event.data === 'authDone') {
            cleanup();
            popup.close();
            resolve();
          }
        });
      };
      window.addEventListener('message', messageListener);
    });
  }

  async initHydrateDevice(reset = false): Promise<void> {
    const client = this.clientService.client;
    if (!client) {
      throw new Error('Kein Client aktiv');
    }

    const crypto = client.getCrypto();
    if (!crypto) {
      throw new Error('Keine Krypto-api');
    }
    const isDehydrationSupported = await crypto.isDehydrationSupported();
    if (!isDehydrationSupported) {
      throw new Error('Dehydration wird vom Server nicht unterstützt');
    }
    await crypto.startDehydration({
      rehydrate: !reset,
      createNewKey: reset
    });
  }

  protected async processCrossSigning(
    ready: boolean,
    reset: boolean,
    crypto: CryptoApi,
    client: MatrixClient
  ): Promise<boolean | void> {
    if (ready && !reset) {
      return; // nichts zu tun (wie of(undefined))
    }

    await crypto.bootstrapCrossSigning({
      setupNewCrossSigning: reset,
      authUploadDeviceSigningKeys: async makeRequest => {
        try {
          await makeRequest({});
        } catch (error: any) {
          if (error?.httpStatus === 401) {
            await this.openReAuth(error?.data?.session);
            await makeRequest({ session: error?.data?.session });
          } else {
            throw error;
          }
        }
      }
    });

    const isReady = await crypto.isCrossSigningReady();
    const userId = client.getUserId();
    if (!userId) {
      throw new Error('No UserId found when calling processCrossSigning');
    }
    const deviceId = client.getDeviceId();
    if (!deviceId) {
      throw new Error('No DeviceId found when calling processCrossSigning');
    }
    await crypto.setDeviceVerified(userId, deviceId, true);

    return isReady;
  }

  private async bootstrapSecretStorageWithReset(crypto: CryptoApi): Promise<void> {
    await crypto.bootstrapSecretStorage({
      setupNewSecretStorage: true,
      setupNewKeyBackup: true,
      createSecretStorageKey: this.createSecretStorageKeyCallback.bind(this)
    });
  }

  private async bootstrapSecretStorageWithoutReset(crypto: CryptoApi): Promise<void> {
    const backupInfo = await crypto.getKeyBackupInfo();

    await crypto.bootstrapSecretStorage({
      setupNewSecretStorage: !backupInfo,
      setupNewKeyBackup: !backupInfo,
      createSecretStorageKey: this.createSecretStorageKeyCallback.bind(this)
    });
  }

  private async processKeyBackup(crypto: CryptoApi, backupInfo: KeyBackupInfo): Promise<KeyBackupCheck | null> {
    await crypto.isKeyBackupTrusted(backupInfo);
    await crypto.loadSessionBackupPrivateKeyFromSecretStorage();
    await crypto.restoreKeyBackup();
    return await crypto.checkKeyBackupAndEnable();
  }

  private async resetAndEnableKeyBackup(crypto: CryptoApi): Promise<KeyBackupCheck | null> {
    await crypto.resetKeyBackup();
    return await crypto.checkKeyBackupAndEnable();
  }

  /** Erkennt transiente Backup-Fehler im Konsistenzfenster nach einer Schlüsseländerung/einem Reset. */
  private isTransientBackupError(e: unknown): boolean {
    const msg = e instanceof Error ? e.message : String(e);
    return /does not match the decryption key|does not match backup info|key backup on server does not match/i.test(
      msg
    );
  }

  /**
   * Führt eine backup-abhängige Operation aus und wiederholt sie bei transienten Konsistenzfehlern
   * begrenzt mit Backoff. Voraussetzung: Der Schlüssel wurde zuvor via {@link verifyRecoveryKey}
   * bestätigt — andernfalls würde ein Fehlpasswort hier unnötig wiederholt.
   */
  private async retryOnTransientBackupError<T>(operation: () => Promise<T>): Promise<T> {
    for (let attempt = 0; ; attempt++) {
      try {
        return await operation();
      } catch (e) {
        const isLast = attempt >= VerificationService.KEY_BACKUP_RETRY_ATTEMPTS - 1;
        if (isLast || !this.isTransientBackupError(e)) {
          throw e;
        }
        const delays = VerificationService.KEY_BACKUP_RETRY_DELAYS_MS;
        const delay = delays[Math.min(attempt, delays.length - 1)];
        console.warn(
          `KeyBackup: transienter Konsistenzfehler, erneuter Versuch in ${delay} ms (Versuch ${attempt + 1}).`,
          e
        );
        await new Promise(resolve => setTimeout(resolve, delay));
      }
    }
  }

  /**
   * Callback zur Ableitung des Recovery-Schlüssels aus einer Passphrase
   */
  private async createSecretStorageKeyCallback(): Promise<GeneratedSecretStorageKey> {
    const client = this.clientService.client;
    if (!client) {
      throw new Error('Matrix-Client nicht verfügbar');
    }
    const crypto = client.getCrypto();
    if (!crypto) {
      throw new Error('Crypto-API nicht initialisiert');
    }
    const verificationPassword = this.clientService.getVerificationPassword();

    if (!verificationPassword) {
      throw new Error('Keine Passphrase zur Erzeugung des Recovery-Schlüssels vorhanden');
    }

    return crypto.createRecoveryKeyFromPassphrase(verificationPassword); // relevant für A_25616
  }
}
